import React, { useState, ChangeEvent, useCallback, useEffect, useRef } from "react";

import { useAccount } from "@shared/contexts";

import { useNavigate } from "react-router-dom";

import { AuthenticationForm, Field as FormField, CheckBox as FormCheckBox, CountrySelect } from "@components/ui";


import { ComposeURLWithGetQueryArguments } from "@shared/utils/navigation/get";
import { PagePaths } from "@shared/constants";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";
import { t_createAccount } from "@i18n/usualTexts/t_createAccount";
import { t_help } from "@i18n/usualTexts/t_help";
import i18n from "@i18n/index";
import { cleanFormDataErrors } from "@shared/utils/form/FormDataServices";
import { set_localStorage as setSessionStorage } from "@shared/utils";
import { StartTransactionRequest } from "@features/createaccount/service/AccountRequest";
import { useOAuth2 } from "@shared/contexts";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";
import { delay, intervalMiliseconds } from "@shared/utils";
import { scheduleSubmittedReset } from "@shared/utils";
import { useAuthError } from "@shared/hooks/useAuthError";
import { getSessionFormValue } from "@shared/utils/storage/sessionFormStorage";
import { getHousienUrl } from "@shared/utils/housienUrl";

function firstDefined(...values: (string | undefined | null)[]): string {
  for (const v of values) {
    if (v != null) return v;
  }
  return "";
}

function buildInitialFormData(accountData: any): FormData {
  return {
    name: {
      value: firstDefined(accountData.name, getSessionFormValue("name")),
      errors: new Set<string>(),
    },
    surname: {
      value: firstDefined(accountData.surname, getSessionFormValue("surname")),
      errors: new Set<string>(),
    },
    nationality: {
      value: firstDefined(accountData.nationality, getSessionFormValue("nationality")),
      errors: new Set<string>(),
    },
    acceptTerms: {
      value: firstDefined(accountData.acceptTerms, getSessionFormValue("acceptTerms")),
      errors: new Set<string>(),
    },
  };
}

interface FormData {
  name: {
    value: string;
    errors: Set<string>;
  };
  surname: {
    value: string;
    errors: Set<string>;
  };
  nationality: {
    value: string;
    errors: Set<string>;
  };
  acceptTerms: {
    value: string;
    errors: Set<string>;
  };
}


function useRecaptchaInit(
  antiBotToken: string | null,
  executeRecaptcha: (() => Promise<any>) | undefined,
  isRecaptchaReadyCheck: (() => boolean) | undefined,
) {
  const [isRecaptchaReady, setIsRecaptchaReady] = useState<boolean>(false);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let animationFrameId: number | null = null;
    const maxWaitTime = 10000;
    const startTime = Date.now();

    const isReadyNow = (): boolean => !!(isRecaptchaReadyCheck && isRecaptchaReadyCheck());
    const isTimedOut = (): boolean => Date.now() - startTime > maxWaitTime;

    const waitForRecaptchaReady = (): Promise<boolean> => {
      return new Promise((resolve) => {
        const checkReady = () => {
          if (!isMounted) { resolve(false); return; }
          if (isReadyNow()) { resolve(true); return; }
          if (isTimedOut()) { resolve(false); return; }
          animationFrameId = requestAnimationFrame(checkReady);
        };
        checkReady();
      });
    };

    const executeRecaptchaToken = async () => {
      if (!executeRecaptcha) {
        throw new Error(i18n.t('error.runtime.recaptchaUnavailable'));
      }
      const isReady = await waitForRecaptchaReady();
      if (!isReady) {
        throw new Error(i18n.t('error.runtime.recaptchaNotReadyTimeout'));
      }
      const recaptchaResult = await executeRecaptcha();
      if (!recaptchaResult.success) {
        throw new Error(`${i18n.t('error.runtime.recaptchaFailed')}: ${recaptchaResult.error || 'Unknown error'}`);
      }
    };

    const setReady = () => { if (isMounted) { setIsRecaptchaReady(true); setRecaptchaError(null); } };

    const initializeRecaptcha = async () => {
      try {
        if (antiBotToken) { setReady(); return; }
        await executeRecaptchaToken();
        setReady();
      } catch (error) {
        if (!isMounted) return;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setRecaptchaError(errorMessage);
        setIsRecaptchaReady(false);
      }
    };

    initializeRecaptcha();

    return () => {
      isMounted = false;
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
    };
  }, [antiBotToken, executeRecaptcha, isRecaptchaReadyCheck]);

  return { isRecaptchaReady, recaptchaError };
}

const CreateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useDocumentTitle(t("signup.createAccount.pageTitle"));
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const submittedResetRef = useRef<number | null>(null);

  const { accountData, setAccountData } = useAccount();
  const { oauth2Data } = useOAuth2();
  const { currentToken: antiBotToken, executeRecaptcha, isReady: isRecaptchaReadyCheck } = useRecaptchaContext();
  const { handleRegisterError } = useAuthError();
  const { isRecaptchaReady, recaptchaError } = useRecaptchaInit(antiBotToken, executeRecaptcha, isRecaptchaReadyCheck);
  const [formData, setFormData] = useState<FormData>(() => buildInitialFormData(accountData));

  useEffect(() => {
    return () => {
      if (submittedResetRef.current !== null) {
        cancelAnimationFrame(submittedResetRef.current);
      }
    };
  }, []);

  const frontExceptionFunction = useCallback((errorDescription: Array<string>) => {
    formData[errorDescription[0] as keyof FormData].errors.add(
      errorDescription[1]
    );
  }, [formData]);

  const handleFormData = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;

    formData[name as keyof FormData].value = value;
    setFormData({
      ...formData,
    });
    cleanFormDataErrors(formData);
  }, [formData]);

  const post_and_navigate = useCallback(async () => {
    if (!isRecaptchaReady) {
      throw new Error(t('error.recaptcha.notReady'));
    }

    const tokenToUse = antiBotToken;
    if (!tokenToUse) {
      throw new Error(t('error.recaptcha.tokenNotAvailable'));
    }
    
    const startTransactionPostResponse = await StartTransactionRequest(oauth2Data, tokenToUse);
    console.log(startTransactionPostResponse);

    const intervalMilisecondsValue = intervalMiliseconds(startTransactionPostResponse);
    await delay(intervalMilisecondsValue);

    const step = 2;
    const newAccountData = {
      ...accountData,
      name: formData.name.value,
      surname: formData.surname.value,
      nationality: formData.nationality.value,
      acceptTerms: formData.acceptTerms.value,
      step: step,
      startTransactionPostResponse: startTransactionPostResponse,
    };

    setAccountData(newAccountData);
    navigate(ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.BIRTHDAYGENDER));
  }, [accountData, formData, setAccountData, navigate, oauth2Data, antiBotToken, isRecaptchaReady]);

  const checkThisFormData = useCallback(() => {
    // Clear previous errors
    formData.name.errors.clear();
    formData.surname.errors.clear();
    formData.nationality.errors.clear();
    formData.acceptTerms.errors.clear();

    let isValid = true;

    // Basic validation - you can add more specific validation here
    if (!formData.name.value.trim()) {
      formData.name.errors.add(t(t_help.fieldOrSelect, {placeholder: t(t_createAccount.namePlaceholder)}));
      isValid = false;
    }
    // Surname is optional, so no validation needed
    if (!formData.nationality.value) {
      formData.nationality.errors.add(t(t_help.fieldOrSelect, {placeholder: t(t_createAccount.nationalityPlaceholder)}));
      isValid = false;
    }
    if (!formData.acceptTerms.value) {
      formData.acceptTerms.errors.add(t(t_help.check));
      isValid = false;
    }

    return isValid;
  }, [formData, t]);

  const checkRecaptchaReadiness = useCallback((): boolean => {
    if (isRecaptchaReady) return true;
    setServerErrorPopUp(recaptchaError || t('help.recaptchaWaiting'));
    return false;
  }, [isRecaptchaReady, recaptchaError, t]);

  const handleSubmit = useCallback(async () => {
    if (!checkRecaptchaReadiness()) return;

    setIsLoading(true);
    setSubmitted(true);

    try {
      const isValid = checkThisFormData();
      
      if (!isValid) {
        setIsLoading(false);
        return;
      }

      await post_and_navigate();
    } catch (e) {
      const error = JSON.parse(JSON.stringify(e));
      handleRegisterError(error, setServerErrorPopUp, frontExceptionFunction);
      setIsLoading(false);
    } finally {
      set_localStorage(formData);
      setFormData({ ...formData });
      scheduleSubmittedReset(submittedResetRef, setSubmitted, 1000);
    }
  }, [formData, checkThisFormData, post_and_navigate, checkRecaptchaReadiness, frontExceptionFunction, handleRegisterError]);

  function handleCheckBox(e: ChangeEvent<HTMLInputElement>) {
    const { name } = e.target;

    formData[name as keyof FormData].value = formData[name as keyof FormData]
      .value
      ? ""
      : "true";
    setFormData({
      ...formData,
    });
  }

  function handleFormSelect(selectValue: string, selectName: string) {
    formData[selectName as keyof FormData].value = selectValue;
    setFormData({
      ...formData,
    });
    cleanFormDataErrors(formData);
  }

  function set_localStorage(formData: FormData) {
    // Use the set_localStorage from FormDataServices which uses session storage
    setSessionStorage(formData);
  }

  function handleTermsClick() {
    window.open(`${getHousienUrl()}/serviceterms/latest`, "_blank");
  }

  function handlePrivacyPolicyClick() {
    window.open(`${getHousienUrl()}/privacy/latest`, "_blank");
  }

  function back() {
    navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.IDENTITY));
  }

  // Show loading state while reCAPTCHA is initializing
  const isFormLoading = isLoading || !isRecaptchaReady;

  return (
    <AuthenticationForm
      onSubmit={handleSubmit}
      isLoading={isFormLoading}
      back={back}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
    >
      <FormField
        autofocus={true}
        name="name"
        type="text"
        required={true}
        onChange={handleFormData}
        value={formData.name.value}
        errors={formData.name.errors}
        submitted={submitted}
      >
        {t(t_createAccount.namePlaceholder)}
      </FormField>

      <FormField
        name="surname"
        type="text"
        required={false}
        onChange={handleFormData}
        value={formData.surname.value}
        errors={formData.surname.errors}
        submitted={submitted}
      >
        {t(t_createAccount.surnamePlaceholder)}
      </FormField>

      <CountrySelect
        name="nationality"
        value={formData.nationality.value}
        onChange={handleFormSelect}
        errors={formData.nationality.errors}
        submitted={submitted}
        required={true}
      >
        {t(t_createAccount.nationalityPlaceholder)}
      </CountrySelect>

      <FormCheckBox
        name="acceptTerms"
        onChange={handleCheckBox}
        value={formData.acceptTerms.value}
        errors={formData.acceptTerms.errors}
        submitted={submitted}
        required={true}
        termsLink={t(t_createAccount.serviceTermsLink)}
        onTermsClick={handleTermsClick}
        privacyPolicyLink={t(t_createAccount.privacyPolicyLink)}
        onPrivacyPolicyClick={handlePrivacyPolicyClick}
      >
        {t(t_createAccount.serviceTermsCheck)}
      </FormCheckBox>

    </AuthenticationForm>
  );
};

export default CreateAccountPage;